import css from './PhoneForm.module.scss'
import React, { useEffect, useState } from 'react'
import { usePost } from 'api'

import { EnvType } from 'meta/app'
import DataTestId from 'meta/dataTestId'
import { FormBuilder, HiddenBuilder, PhoneBuilder, TextBuilder } from 'meta/form'
import { ICheckoutDetails } from 'meta/pages/checkout'
import { LoginProvider } from 'meta/pages/login'
import { ApiEndpoint } from 'controller/endpoint'
import { useAppDispatch } from 'store'
import { AuthActions } from 'store/auth'
import { CookieName } from 'utils/cookie'
import { CookieClient, getFastCheckoutUserObject, setFastCheckoutUserObject } from 'utils/cookie/client'

import OTPForm from 'components/Auth/OTPV3/Form'
import OTPFormHeader from 'components/Auth/OTPV3/Header'
import Form, { IFormOnSuccess, IFormValues } from 'components/Forms/Form'
import Img from 'components/Media/Img'
import { useCheckoutDetails, useFastCheckoutCreditCard } from 'hooks/useCheckout'
import { useAppEnv, useEnableRecaptcha } from 'hooks/useEnv'

interface IUserProps {
  userData?: IEmailData & IPhoneData
}

interface IProps {
  onSuccess: IFormOnSuccess
}

interface IEmailData {
  email: string
}

interface IPhoneData {
  phone: string
  phonePrefix: string
}

interface ILoginResponseData {
  token: string
  otpToken: string
}

interface IOtpData {
  otpToken: string
  otp?: string
  hasUser: boolean
}

enum FastCheckoutSteps {
  phone = 'phone',
  otp = 'otp',
}

const PhoneFormFastCheckoutContainer: React.FC<IProps> = (props) => {
  const { onSuccess } = props

  const [userData, setUserData] = useState<(IEmailData & IPhoneData) | undefined>(undefined)
  const [formReady, setFormReady] = useState<boolean>(false)

  useEffect(() => {
    const userData = getFastCheckoutUserObject()

    if (userData) {
      setUserData(userData)
    } else {
      setFormReady(true)
    }
  }, [])

  useEffect(() => {
    if (userData) {
      setFormReady(true)

      if (userData.email) {
        onSuccess({ email: userData.email })
      }
    }
  }, [userData])

  if (!formReady) {
    return null
  }

  return <PhoneFormFastCheckout userData={userData} onSuccess={onSuccess} />
}

const PhoneFormFastCheckout: React.FC<IProps & IUserProps> = (props) => {
  const { onSuccess, userData } = props

  const [emailData, setEmailData] = useState<IEmailData | null>(null)
  const [phoneData, setPhoneData] = useState<IPhoneData | null>(null)
  const [otpData, setOtpData] = useState<IOtpData | null>(null)
  const [currentStep, setCurrentStep] = useState<FastCheckoutSteps>(FastCheckoutSteps.phone)

  const { fetch } = usePost()

  const creditCard = useFastCheckoutCreditCard()

  const enableRecaptcha = useEnableRecaptcha()

  const recaptchaAction = enableRecaptcha ? { action: 'shop/auth/phone' } : null

  const env = useAppEnv()

  const { insideIFrame } = useCheckoutDetails() as ICheckoutDetails
  const loginActionPath = insideIFrame ? ApiEndpoint.users.loginFromIframe() : ApiEndpoint.users.login()

  const dispatch = useAppDispatch()

  const backendRouteField = new HiddenBuilder('backend_route').addValue(ApiEndpoint.users.otp()).build()

  const csrfToken = CookieClient.get(CookieName.CSRF_TOKEN)

  const csrfField = new HiddenBuilder('_csrfToken').addValue(csrfToken).build()

  const insideIframeField = new HiddenBuilder('_insideIframe').addValue(Boolean(insideIFrame).toString()).build()

  // TODO: i valori del telefono, se ci sono, vanno letti dai cookie
  const emptyPhoneField = new PhoneBuilder('phone', 'phonePrefix')
    .addPlaceholder('form:phone')
    .addRequiredValidation()
    .build()

  const emptyEmail = new TextBuilder('email')
    .addOptionalEmailValidation()
    .addPlaceholder('checkout:email_optional_placeholder')
    .build()

  const phoneField = new PhoneBuilder('phone', 'phonePrefix')
    .addValuePrefix(userData && userData.phonePrefix ? userData.phonePrefix : '+39')
    .addValue(userData && userData.phone ? userData.phone : '')
    .addRequiredValidation()
    .addPlaceholder('form:phone')
    .build()

  const email = new TextBuilder('email')
    .addValue(userData && userData.email ? userData.email : '')
    .addPlaceholder('checkout:email_optional_placeholder')
    .build()

  const emptyFormFields = new FormBuilder()
    .addField(emptyPhoneField)
    .addField(emptyEmail)
    .addField(csrfField)
    .addField(backendRouteField)
    .addField(insideIframeField)
    .build()

  const prefilledFormFields = new FormBuilder()
    .addField(phoneField)
    .addField(email)
    .addField(csrfField)
    .addField(backendRouteField)
    .addField(insideIframeField)
    .build()

  //@ts-ignore
  const handleStepChange = (values: IOtpData, response) => {
    const { hasUser, otpToken, otp } = response.data

    setOtpData({
      otpToken: otpToken,
      otp: otp,
      hasUser: hasUser,
    })
  }

  useEffect(() => {
    if (otpData) {
      setCurrentStep(FastCheckoutSteps.otp)
    }
  }, [otpData])

  const resetPhoneForm = () => {
    setPhoneData(null)
    setEmailData(null)
    setOtpData(null)
    setCurrentStep(FastCheckoutSteps.phone)
  }

  //@ts-ignore
  const handleOTPSuccess = async (values, response) => {
    const { token, otpToken } = response.data as ILoginResponseData

    if (otpData?.hasUser) {
      // set cookie and redirect if needed
      CookieClient.set(CookieName.TOKEN, token, { expires: 30 })

      // update store
      dispatch(AuthActions.login({ token }))
    }

    // set fast checkout OTP token
    CookieClient.set(CookieName.FAST_CHECKOUT_OTP_TOKEN, otpToken, { expires: 60 * 60 * 6 })

    // save fast checkout credit card
    if (creditCard) {
      await fetch({
        extraHeaders: { 'Tsms-Guest-Token': otpToken },
        useV4: true,
        urlResource: ApiEndpoint.v4.guest.saveCard(creditCard.id),
      })
    }

    setFastCheckoutUserObject({
      phone: phoneData?.phone,
      phonePrefix: phoneData?.phonePrefix,
      email: emailData?.email,
    })

    onSuccess({ email: emailData?.email, hasUser: otpData?.hasUser })
  }

  const OTPBodyParser = (otp: string, otpToken: string) => {
    let body: object = { otp, otpToken }

    if (otpData?.hasUser) {
      body = {
        ...body,
        provider: LoginProvider.OTP,
        providerData: {
          otp,
          otpToken,
          phone: phoneData?.phone,
          phonePrefix: phoneData?.phonePrefix,
        },
      }
    }
    return body
  }

  return (
    <div className={css.fast_checkout_phoneform_wrapper}>
      {currentStep == FastCheckoutSteps.phone && (
        <div className={css.phoneBox}>
          <Form
            //@ts-ignore
            onSuccess={handleStepChange}
            bodyParser={(fieldValues: IFormValues) => {
              const { phone, phonePrefix, email } = fieldValues

              setPhoneData({
                phone: phone,
                phonePrefix: phonePrefix,
              })

              setEmailData({
                email: email && email !== '' ? email : `${String(phonePrefix).replace('+', '')}.${phone}@ticketsms.it`,
              })

              return fieldValues
            }}
            formFields={userData ? prefilledFormFields : emptyFormFields}
            action={ApiEndpoint.users.otp()}
            //@ts-ignore
            recaptcha={recaptchaAction}
          />
        </div>
      )}

      {currentStep == FastCheckoutSteps.otp && phoneData && otpData && (
        <div className={css.otpBox}>
          <div className={css.header}>
            <button className={css.goBack} onClick={resetPhoneForm}>
              <Img alt="" src="/icons/arrow_left_white.svg" />
            </button>

            <OTPFormHeader noMargin phone={phoneData.phone} phonePrefix={phoneData.phonePrefix} />
          </div>
          {[EnvType.certification.toString(), EnvType.development.toString(), EnvType.staging.toString()].includes(
            env
          ) &&
            otpData.otp && (
              <div className={css.otpCodeBox}>
                <h3>
                  OTP: <span data-testid={DataTestId.login.otpCode}>{otpData.otp}</span>
                </h3>
              </div>
            )}

          <OTPForm
            action={otpData && Boolean(otpData.hasUser) ? loginActionPath : ApiEndpoint.users.otpVerify()}
            actionResend={ApiEndpoint.users.otp()}
            bodyParser={OTPBodyParser}
            onSuccess={handleOTPSuccess}
            //@ts-ignore
            authToken={null}
            otpToken={otpData.otpToken}
            phone={Number(phoneData.phone)}
            phonePrefix={phoneData.phonePrefix}
            onBlackBg
          />
        </div>
      )}
    </div>
  )
}

export default PhoneFormFastCheckoutContainer
